/** WebSocket close code 4005, "organisation changed" (VC-Authz set O2; Tom,
 *  2026-10-12: a user acts as one organisation at a time): this browser
 *  session switched organisation, or started or stopped impersonating, since
 *  the socket connected, and a socket acts as the organisation it connected
 *  with. Not an error.
 *
 *  The page RELOADS, once, rather than reconnecting in place: everything
 *  this tab holds was read as the old organisation — the graph store, the
 *  held draft segments and state packs, the computed and claims channels'
 *  URLs (which name the reader) — and the islands the page names may not be
 *  the new organisation's. A reload drops all of it at once, as starting or
 *  stopping impersonation already does, and the new connection acts as the
 *  session's new organisation. The other close codes keep reconnecting. */

export const CLOSE_ORGANISATION_CHANGED = 4005;

/** A 4005 that keeps coming (VC-Authz's E2): a switch whose answer was lost
 *  (reason `organisation_changed`), or a session the server cannot read
 *  (`session_unverifiable`), would reload the page for good. The reloads are
 *  counted in sessionStorage (a page's own count dies with it); past
 *  RELOADS_ALLOWED within RELOAD_WINDOW_MS the page stops — no reload, no
 *  reconnect — and a prompt says what to do. The count goes after a socket
 *  stays connected that long, or a switch succeeds. Without storage the
 *  page reloads as before. */
export const RELOADS_ALLOWED = 3;
export const RELOAD_WINDOW_MS = 60_000;
const RELOADS = "sp-organisation-change-reloads";

export type OrganisationChangeStuck = "organisation_changed" | "session_unverifiable";

let reloading = false;
let reloadRequested = false;
let pendingCookieRequests = 0;
let reloadPage: () => void = () => window.location.reload();
let stuck: OrganisationChangeStuck | null = null;
const stuckListeners = new Set<(reason: OrganisationChangeStuck) => void>();

/** the 4005 reloads of this tab within the window, or null without storage */
function recentReloads(now: number): number[] | null {
  try {
    const list: unknown = JSON.parse(sessionStorage.getItem(RELOADS) ?? "[]");
    return Array.isArray(list) ? list.filter((t): t is number => typeof t === "number" && now - t < RELOAD_WINDOW_MS) : [];
  } catch {
    return null;
  }
}

export function clearOrganisationChangeReloads(): void {
  try { sessionStorage.removeItem(RELOADS); } catch { /* no storage */ }
}

/** A socket connected: once it has stayed so for the window, the count
 *  goes. Answers the cancel for its close. */
export function socketConnectedSteadily(): () => void {
  const t = setTimeout(clearOrganisationChangeReloads, RELOAD_WINDOW_MS);
  return () => clearTimeout(t);
}

/** Why this page stopped reloading on 4005, or null. */
export function organisationChangeStuck(): OrganisationChangeStuck | null {
  return stuck;
}

export function onOrganisationChangeStuck(listener: (reason: OrganisationChangeStuck) => void): () => void {
  stuckListeners.add(listener);
  return () => { stuckListeners.delete(listener); };
}

/** Answers whether the close was an organisation change (then the page is
 *  reloading, or has stopped with a prompt, and the caller must not
 *  reconnect). Both sockets of a tab may see it: the page reloads once. */
export function organisationChanged(event: { code?: number; reason?: string } | undefined): boolean {
  if (event?.code !== CLOSE_ORGANISATION_CHANGED) return false;
  if (stuck) return true;
  if (!reloadRequested) {
    const now = Date.now();
    const recent = recentReloads(now);
    if (recent && recent.length >= RELOADS_ALLOWED) {
      const reason: OrganisationChangeStuck = event.reason === "session_unverifiable" ? "session_unverifiable" : "organisation_changed";
      stuck = reason;
      for (const l of Array.from(stuckListeners)) l(reason);
      return true;
    }
    if (recent) {
      try { sessionStorage.setItem(RELOADS, JSON.stringify([...recent, now])); } catch { /* no storage */ }
    }
  }
  reloadRequested = true;
  flushReload();
  return true;
}

function flushReload(): void {
  if (reloadRequested && !reloading && pendingCookieRequests === 0) {
    reloading = true;
    reloadPage();
  }
}

/** Another worker may close the socket as soon as Redis changes, before
 * this tab receives Set-Cookie. Stop reconnecting immediately, but let the
 * session-changing fetch receive its headers before reloading the tab.
 * This does not delay server revocation or allow work on the old socket. */
export async function sessionCookieFetch(...args: Parameters<typeof fetch>): Promise<Response> {
  pendingCookieRequests++;
  try {
    return await fetch(...args);
  } finally {
    pendingCookieRequests--;
    flushReload();
  }
}

/** tests only: a fresh page, reloading with `reload` */
export function resetOrganisationChangeForTests(reload: () => void, { keepCount = false } = {}): void {
  reloading = false;
  reloadRequested = false;
  pendingCookieRequests = 0;
  reloadPage = reload;
  stuck = null;
  if (!keepCount) clearOrganisationChangeReloads();
}
